import {
    Box,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    IconButton,
    Pagination,
    Card,
    CardContent
  } from '@mui/material';
  import { useState, useMemo } from 'react';
  import RecommendedUserCard from '../cards/RecommendedUserCard';
  import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
  import ChevronRightIcon from '@mui/icons-material/ChevronRight';
  import SearchOffIcon from '@mui/icons-material/SearchOff';
  import PersonSearchIcon from '@mui/icons-material/PersonSearch';
  
const RecommendedUsers = ({ 
  users = [], 
  loading = false, 
  onHeartClick, 
  onInterested,
  onPurchase, 
  theme,
  hasSubscription = false,
  isLoggedIn = false,
  title = "Recommended Users"
}) => {
    const [sortBy, setSortBy] = useState('name');
    const [currentPage, setCurrentPage] = useState(1);
    const profilesPerPage = 6;
  
    // Memoize sorted users
    const sortedUsers = useMemo(() => {
      return [...users].sort((a, b) => {
        switch (sortBy) {
          case 'name':
            return (a.first_name || a.name || '').localeCompare(b.first_name || b.name || '');
          case 'age':
            return (a.age || 0) - (b.age || 0);
          case 'liked_count':
            return (b.liked_count || 0) - (a.liked_count || 0);
          default:
            return 0;
        }
      });
    }, [users, sortBy]);

    // Calculate pagination
    const totalPages = Math.ceil(sortedUsers.length / profilesPerPage);
    const startIndex = (currentPage - 1) * profilesPerPage;
    const endIndex = startIndex + profilesPerPage;
    const currentUsers = sortedUsers.slice(startIndex, endIndex);

    // Reset to first page when sort changes
    const handleSortChange = (newSortBy) => {
      setSortBy(newSortBy);
      setCurrentPage(1);
    };

    const handlePageChange = (event, page) => {
      setCurrentPage(page);
    };
  
    return (
      <Box sx={{ 
        mt: 6, 
        p: { xs: 2, md: 4 }, 
        borderRadius: 6, 
        background: `linear-gradient(135deg, ${theme.palette.primary.light}11 0%, ${theme.palette.secondary?.light || '#fce4ec'}22 100%)`, 
        boxShadow: '0 8px 32px rgba(62,2,23,0.07)' 
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            {title}
          </Typography>
          {/* Sort dropdown */}
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              label="Sort by"
              size="small"
            >
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="age">Age</MenuItem>
              <MenuItem value="liked_count">Most Liked</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            py: 8,
            textAlign: 'center'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              mb: 2
            }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  border: `3px solid ${theme.palette.primary.light}`,
                  borderTop: `3px solid ${theme.palette.primary.main}`,
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }}
              />
              <Typography variant="h6" sx={{ 
                color: theme.palette.primary.main,
                fontWeight: 600
              }}>
                Searching Profiles...
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ 
              color: theme.palette.text.secondary,
              maxWidth: 300
            }}>
              Please wait while we find the best matches for you
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={4} sx={{ alignItems: 'stretch', position: 'relative', minHeight: '80vh' }}>
              {sortedUsers.length === 0 ? (
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '100%',
                  maxWidth: 450,
                  px: 2
                }}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      width: '100%',
                      textAlign: 'center',
                      background: `linear-gradient(135deg, ${theme.palette.primary.light}08 0%, ${theme.palette.secondary?.light || '#fce4ec'}15 100%)`,
                      border: `2px dashed ${theme.palette.primary.light}`,
                      borderRadius: 4,
                      p: 4,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          mb: 3,
                          '& .MuiSvgIcon-root': {
                            fontSize: '4rem',
                            color: theme.palette.primary.light,
                            opacity: 0.7
                          }
                        }}
                      >
                        {title.includes('Search Results') ? <SearchOffIcon /> : <PersonSearchIcon />}
                      </Box>
                      
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          mb: 2,
                          fontSize: '1.4rem'
                        }}
                      >
                        {title.includes('Search Results') ? 'No Profiles Found' : 'No Recommendations'}
                      </Typography>
                      
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          mb: 3,
                          lineHeight: 1.6
                        }}
                      >
                        {title.includes('Search Results') 
                          ? 'We couldn\'t find any profiles matching your current search criteria.' 
                          : 'We don\'t have any recommendations for you at the moment.'}
                      </Typography>
                      
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: theme.palette.text.disabled,
                          fontStyle: 'italic',
                          fontSize: '0.9rem'
                        }}
                      >
                        {title.includes('Search Results') 
                          ? '💡 Try adjusting your search filters or expanding your criteria to discover more profiles.' 
                          : '💡 Check back later for new recommendations as we add more profiles.'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              ) : (
                currentUsers.map((user) => (
                  <Grid item xs={12} sm={6} md={4} key={user.id} sx={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
                    <RecommendedUserCard 
                      user={user} 
                      handleHeartClick={onHeartClick} 
                      handleInterested={onInterested}
                      handlePurchase={onPurchase}
                      theme={theme}
                      hasSubscription={hasSubscription}
                      isLoggedIn={isLoggedIn}
                    />
                  </Grid>
                ))
              )}
            </Grid>
            
            {/* Pagination Controls */}
            {sortedUsers.length > profilesPerPage && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                mt: 4,
                gap: 2,
                p: 2,
                background: 'rgba(255,255,255,0.1)',
                borderRadius: 3,
                backdropFilter: 'blur(10px)'
              }}>
                <IconButton
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': { bgcolor: theme.palette.primary.dark },
                    '&:disabled': { bgcolor: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.26)' }
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                
                <Typography variant="body1" sx={{ 
                  fontWeight: 600, 
                  color: theme.palette.primary.main,
                  minWidth: '120px',
                  textAlign: 'center'
                }}>
                  Page {currentPage} of {totalPages}
                </Typography>
                
                <IconButton
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': { bgcolor: theme.palette.primary.dark },
                    '&:disabled': { bgcolor: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.26)' }
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };
  
  export default RecommendedUsers;